/**
 * The records under each line of the interview: what the lookups placed
 * under that line returned (`ProbeDeskTurn.callIds`, placed as `placeSources`
 * places pages), each once, most important first: by kind and severity, then
 * every lookup's best record before any lookup's second, as `placeSources`
 * interleaves pages from several searches. Built from what the server
 * returned, never from the words spoken.
 *
 * @module components/apps/probe/desk/lineRecords
 */
import type { ProbeBriefing } from "@amoreg/contracts";

import { recordKey } from "../../../../state/probeRecords";
import { deskLookupsOf, type ProbeDeskCall } from "../ProbeDesk.logic";
import { byImportance, deskRecordsOf, type DeskRecord } from "./deskRecords";

/** Each line's records by the line's key; a line that read nothing is absent. */
export const lineRecordsOf = (
  call: ProbeDeskCall,
  briefing: ProbeBriefing | null,
): ReadonlyMap<string, ReadonlyArray<DeskRecord>> => {
  const lookups = deskLookupsOf(call, briefing);
  const placed = new Map<string, ReadonlyArray<DeskRecord>>();
  for (const turn of call.turns) {
    if (turn.callIds.length === 0) continue;
    const callIds = new Set(turn.callIds);
    const read = lookups.finished.filter((lookup) => callIds.has(lookup.callId));
    const rank = new Map<string, number>();
    for (const lookup of read) {
      if (lookup.kind !== "list" && lookup.kind !== "opened") continue;
      lookup.records.forEach((record, index) => {
        const key = recordKey(record);
        rank.set(key, Math.min(rank.get(key) ?? index, index));
      });
    }
    const records = deskRecordsOf(read, lookups).toSorted(
      (one, other) =>
        byImportance(one, other) || (rank.get(one.key) ?? 0) - (rank.get(other.key) ?? 0),
    );
    if (records.length > 0) placed.set(turn.key, records);
  }
  return placed;
};
